/**
 * The phone guest home (Gabriel 2026-10-20, frame C of docs/mockups/telify-web/phone-guest-home.html):
 * below 768px a visitor who is not signed in opens on the studio's About page, the app's own
 * AboutScreen fed the website's public reads (shared-client-screens D3, card 2 rows H1 to H14).
 * This file is the website's data part.
 *
 * The website's slots: no back button, it is the front door (H1); the logo, the welcome, the name
 * and the Descriptor centered on the photo (H2, Gabriel 2026-10-21, logo frame D), the sheet
 * opening on the tagline (H3); the top three services and See all, and Happening Soon with its
 * event and series cards, in the sheet (H5, H6, H7); the chat row asks a visitor to sign in (H8);
 * no Book a Session at the end (H9); the app's bar, with Sign in for Profile (H10, H11). Nothing
 * here reaches Telegram (scripts/check-web-no-telegram.mjs).
 *
 * Signed in (web-account-pages D8), the same screen is the studio's About page that Home's About
 * card opens: the app's back button in place of the bar, and the chat row opens the messages with
 * the app's own line.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { AboutOfferings } from '@/pages/AboutPage/AboutOfferings';
import { AboutScreen, type AboutContent } from '@/pages/AboutPage/AboutScreen';

import { mostBookedFirst, usePopularServiceIds, useSiteHappeningSoon, useSiteServices, useSiteStudio } from '../api/site';
import { toHappeningItem, toHubService } from './appShapes';
import { usePhoneChat } from './PhoneDetailFrame';
import { PhoneBar } from './PhoneTabsLayout';

export function PhoneGuestHome({ onBack }: { onBack?: () => void } = {}) {
  const navigate = useNavigate();
  // The app's About page reads this under the studio's name (AboutPage).
  const chat = usePhoneChat('We usually reply within a few hours');
  const studioQuery = useSiteStudio();
  const studio = studioQuery.data;
  const servicesQuery = useSiteServices();
  const popularQuery = usePopularServiceIds();
  const happeningQuery = useSiteHappeningSoon();

  const siteServices = useMemo(
    () => (servicesQuery.data && !popularQuery.isPending ? mostBookedFirst(servicesQuery.data, popularQuery.data ?? []) : []),
    [servicesQuery.data, popularQuery.isPending, popularQuery.data],
  );
  const services = useMemo(() => siteServices.map(toHubService), [siteServices]);
  const siteHappening = happeningQuery.data;
  const happening = useMemo(() => (siteHappening ?? []).map(toHappeningItem), [siteHappening]);

  const about = useMemo<AboutContent | undefined>(
    () =>
      studio && {
        name: studio.name,
        tagline: studio.tagline,
        location: studio.location,
        establishedYear: studio.establishedYear,
        story: studio.story,
        photoUrl: studio.aboutPhotoUrl,
        guideHeading: studio.guideHeading,
        guide: studio.guide?.name
          ? {
              name: studio.guide.name,
              role: studio.guide.role,
              credentials: studio.guide.credentials,
              yearsExperience: studio.guide.yearsExperience ?? null,
              photoUrl: studio.guide.photoUrl,
              bio: studio.guide.bio,
            }
          : null,
      },
    [studio],
  );

  const pathOf = (kind: 'event' | 'series', id: string) =>
    siteHappening?.find((i) => (i.kind === 'event' ? kind === 'event' && i.id === id : kind === 'series' && i.seriesId === id))?.path;

  return (
    <AboutScreen
      status={studio ? 'ready' : studioQuery.isError ? 'error' : 'loading'}
      about={about}
      onRetry={() => void studioQuery.refetch()}
      welcome={{ placement: 'photo', logoUrl: studio?.logoUrl ?? null, descriptor: studio?.descriptor ?? null }}
      onBack={onBack}
      chat={{ line: chat.line, onChat: chat.onChat }}
      afterGuide={
        <AboutOfferings
          services={services}
          onOpenService={(s) => {
            const path = siteServices.find((x) => x.id === s.id)?.path;
            if (path) navigate(path);
          }}
          onSeeAllServices={() => navigate('/services')}
          happening={happening}
          onOpenEvent={(id) => {
            const path = pathOf('event', id);
            if (path) navigate(path);
          }}
          onOpenSeries={(seriesId) => {
            const path = pathOf('series', seriesId);
            if (path) navigate(path);
          }}
          onSeeAllEvents={() => navigate('/events')}
        />
      }
      bottomBar={onBack ? undefined : <PhoneBar current="home" />}
      testId="phone-guest-home"
    />
  );
}
